import React, { useState, useEffect, useMemo } from 'react';
import CustomFileUploader from '../CustomFileUploader/CustomFileUploader';
import { uploaderImages } from '../../constants/uploaderImages';
import { useToast } from '../../helpers/hooks';
import { ImageFile } from '../../interfaces/salePost';
import imageCompression from 'browser-image-compression';
import styles from './editImageManager.module.scss';
import Tooltip from '@mui/material/Tooltip';
import { EditImageManagerProps } from '../../interfaces/salePost';



const EditImageManager = ({
  imagesSelected,
  setImages,
  postPublicationsImages,
  deletePublicationImage,
  fetchingDeleteImage,
  onValidityChange
}: EditImageManagerProps) => {
  const [additionalUploaders, setAdditionalUploaders] = useState<number[]>([]);
  const toast = useToast();
  const MAX_IMAGES = 12;
  const requiredIndices = [0, 1, 2, 3, 4];
  
  const hasRequiredImages = requiredIndices.every(index =>
    (imagesSelected || []).some(img => typeof img.index === 'number' && img.index === index && !img?.toDelete)
  );

  const uploaderKeys = ["front", "back", "leftSide", "rightSide", "interior"] as const;
  const uploaderLabels = ["Foto de frente", "Foto de atrás", "Foto lateral izquierdo", "Foto lateral derecho", "Foto del interior"];

  const checkRequiredImages = (images: ImageFile[] = []) => {
    if (!images) {
      onValidityChange?.(false);
      return false;
    }

    const requiredIndices = [0, 1, 2, 3, 4];
    const activeImages = images.filter(img => !img?.toDelete && img.upload === "success");
  
    const hasAllRequired = requiredIndices.every(index =>
      activeImages.some(img => typeof img.index === 'number' && img.index === index)
    );

    if (!hasAllRequired) {
      const missingIndices = requiredIndices.filter(index => 
        !activeImages.some(img => typeof img.index === 'number' && img.index === index)
      );
      const missingLabels = missingIndices.map(index => uploaderLabels[index]);
    } 

    onValidityChange?.(hasAllRequired);
    return hasAllRequired;
  };

  useEffect(() => {
    checkRequiredImages(imagesSelected);
  }, [imagesSelected]);

  const handleDeleteImage = (image: ImageFile) => {
    if (image && image.id) {

      if (!image.id.startsWith('temp-')) {
        deletePublicationImage(image.id);
      }

      const currentImages = imagesSelected || [];
      const updatedImages = currentImages.map(img => {
        if (img.id === image.id) {
          return {
            ...img,
            toDelete: true,
            deleted: true,
            previewURL: ""
          };
        }
        return img;
      });

      setImages(updatedImages);
      checkRequiredImages(updatedImages);
    }
  };

  const anyImageUploading = useMemo(() => {
    return imagesSelected?.some(img => img.upload === "loading");
  }, [imagesSelected]);

  const validateAndLimitFiles = async (files: ImageFile[], startIndex: number) => {
    let currentImages = imagesSelected || [];
    const activeCount = currentImages.filter(img => !img.toDelete).length;

    if (activeCount + files.length > MAX_IMAGES) {
      const availableSlots = MAX_IMAGES - activeCount;
      if (availableSlots <= 0) {
        toast("Ya alcanzaste el máximo de 12 imágenes permitidas.");
        return null;
      }

      const limitedFiles = files.slice(0, availableSlots);
      toast(`Solo puedes subir ${availableSlots} imagen${availableSlots > 1 ? 'es' : ''} más.`);
      return limitedFiles;
    }

    return files;
  };

  const processImageFiles = async (files: ImageFile[]): Promise<any[]> => {
    return Promise.all(
      files.map(async (file) => {
        try {
          if (!file.file) return file;

          const originalImage = await createImageBitmap(file.file);
          const { canvas, ctx } = createCanvasWithImage(originalImage);

          const recreatedBlob = await canvasToBlob(canvas, file.file.type);
          const compressedFile = await compressImage(recreatedBlob, originalImage);
          const previewURL = URL.createObjectURL(compressedFile);

          return {
            ...file,
            file: compressedFile,
            previewURL,
            name: file.name,
            id: file.id,
          };
        } catch (error) {
          console.error("Error procesando imagen:", error);
          return file;
        }
      })
    );
  };

  const createCanvasWithImage = (image: ImageBitmap) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("No se pudo obtener el contexto del canvas.");

    ctx.drawImage(image, 0, 0);
    return { canvas, ctx };
  };

  const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo crear el Blob desde el canvas"));
      }, mimeType || "image/jpeg", 0.95);
    });
  };

  const compressImage = async (blob: Blob, originalImage: ImageBitmap) => {
    const options = {
      maxSizeMB: 9,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      preserveExif: false
    };

    if (blob.size > 20 * 1024 * 1024) {
      options.maxWidthOrHeight = 1600;
    }

    try {
      const compressedFile = await imageCompression(blob, options);
      return compressedFile;
    } catch (error) {
      const compressedFile = await imageCompression(blob, {
        ...options,
        maxWidthOrHeight: 1200,
        initialQuality: 0.7
      });
      return compressedFile;
    }
  };

  const manageImageIndexes = (files: ImageFile[], startIndex: number) => {
    let currentImages = imagesSelected || [];
    let allImages = [...currentImages];
    let newFiles: ImageFile[] = [];

    if (startIndex <= 4) {
      newFiles = files.map(file => ({
        ...file,
        index: startIndex,
        id: file.id || `temp-${startIndex}-${Date.now()}`
      }));
      allImages = allImages.filter(img => img.index !== startIndex);
    } else if (startIndex === 5) {
      const usedIndices = allImages
        .filter(img => typeof img.index === 'number' && img.index >= 5 && !img.toDelete)
        .map(img => img.index as number);

      let nextIndex = 5;
      while (usedIndices.includes(nextIndex) && nextIndex < MAX_IMAGES) {
        nextIndex++;
      }

      newFiles = files.map((file, i) => {
        const index = Math.min(nextIndex + i, MAX_IMAGES - 1);
        return {
          ...file,
          index,
          id: file.id || `temp-${index}-${Date.now()}`
        };
      });
    } else {

      newFiles = files.map(file => ({
        ...file,
        index: startIndex,
        id: file.id || `temp-${startIndex}-${Date.now()}`
      }));
      allImages = allImages.filter(img => img.index !== startIndex);
    }

    const updatedImages = [...allImages, ...newFiles];
    checkRequiredImages(updatedImages);
    return updatedImages;
  };

  const uploadImages = async (images: ImageFile[]) => {
    
    const uploadedImages = images.map(img => {
     
      if (img?.previewURL && img?.file && img.upload !== "success" && !img.toDelete) {
        
        const formData = new FormData();
        formData.append("file", img.file, img.name);

        postPublicationsImages(formData, img.id).then((res: any) => {
          
          if (res?.data?.file?.path) {
            setImages((prevImages: ImageFile[]) =>
              prevImages.map((image: ImageFile) =>
                image.id === img.id
                  ? {
                    ...image,
                    id: res.data.file.id, 
                    previewURL: res.data.file.path,
                    upload: "success",
                    path: res.data.file.path,
                    file: undefined,
                  } as ImageFile
                  : image
              )
            );
          }
        }).catch((error: any) => {

          setImages((prevImages: ImageFile[]) => 
            prevImages.map((image: ImageFile) =>
              image.id === img.id
                ? {
                  ...image,
                  upload: "failure",
                } as ImageFile
                : image
            )
          );
        });

        return { ...img, upload: "loading" } as ImageFile;
      }
      
      return img;
    });

    setImages(uploadedImages);
  };

  const updateAdditionalUploaders = (images: ImageFile[]) => {
    const extraImages = images.filter(img => typeof img.index === 'number' && img.index >= 5 && !img.toDelete);
    if (extraImages.length === 0) {
      setAdditionalUploaders([]);
      return;
    }

    const maxIndex = Math.max(...extraImages.map(img => typeof img.index === 'number' ? img.index : 5));
    const totalActive = images.filter(img => !img.toDelete).length;
    
    const uploaderCount = totalActive >= MAX_IMAGES
      ? maxIndex - 4
      : maxIndex - 4;

    setAdditionalUploaders(Array.from({ length: Math.max(uploaderCount, 1) }, (_, i) => i));
  };

  const onChangeFileUploader = async (files: ImageFile[], startIndex: number) => {
    try {
      const validatedFiles = await validateAndLimitFiles(files, startIndex);
      if (!validatedFiles) return;

      const processedFiles = await processImageFiles(validatedFiles);
      const tempFiles = processedFiles.map(file => ({
        ...file,
        upload: "loading" as "loading" | "success" | "failure",
        id: file.id || `temp-${startIndex}-${Date.now()}`
      }));

      const updatedImages = manageImageIndexes(tempFiles, startIndex);
      setImages(updatedImages);
      
      await uploadImages(updatedImages);
      updateAdditionalUploaders(updatedImages);
    } catch (error) {
      console.error("Error en onChangeFileUploader:", error);
    }
  };

  useEffect(() => {
    if (!imagesSelected || imagesSelected.length === 0) {
      setAdditionalUploaders([]);
      return;
    }

    const activeAdditional = imagesSelected.filter(
      img => typeof img.index === 'number' && img.index >= 5 && !img.toDelete
    );

    if (activeAdditional.length === 0) {
      setAdditionalUploaders([]);
      return;
    }

    const maxIndex = Math.max(...activeAdditional.map(img => typeof img.index === 'number' ? img.index : 5));
    const totalActive = imagesSelected.filter(img => !img.toDelete).length;
    
    const uploaderCount = totalActive >= MAX_IMAGES
      ? maxIndex - 5
      : maxIndex - 4;

    setAdditionalUploaders(Array.from({ length: Math.max(uploaderCount, 1) }, (_, i) => i));
  }, [imagesSelected]);

  return (
    <div className={styles.editImageManagerContainer}>
      <div className={styles.uploadersGrid}>
        {requiredIndices.map((index) => {
          const hasImage = imagesSelected?.some(img => img.index === index && !img.toDelete && img.upload === "success");
          
          return (
            <div key={index} className={styles.uploaderItem}>
              <h3>
                {uploaderLabels[index]} *
                
              </h3>
              <div className={index === 0 ? styles.coverPhotoWrapper : undefined}>
                <CustomFileUploader
                  values={imagesSelected?.filter(img => img.index === index) || []}
                  onChange={(files: ImageFile[]) => onChangeFileUploader(files, index)}
                  onDeleteItem={handleDeleteImage}
                  className={styles.fileUploader}
                  placeholderImage={uploaderImages[uploaderKeys[index]]}
                  loading={imagesSelected?.some(img => img.index === index && img.upload === "loading")}
                  deleting={fetchingDeleteImage === "loading"}
                />
                {index === 0 && <div className={styles.coverBadge}>Portada</div>}
              </div>
            </div>
          );
        })}

        <Tooltip
          title="Debes subir las fotos obligatorias para poder agregar adicionales"
          disableHoverListener={hasRequiredImages}
          arrow
        >
          <div className={styles.uploaderItem}>
            <h3>Foto adicional</h3>
            <CustomFileUploader
              values={imagesSelected?.filter(img => img.index === 5) || []}
              onChange={(files: ImageFile[]) => onChangeFileUploader(files, 5)}
              onDeleteItem={handleDeleteImage}
              className={styles.fileUploader}
              placeholderImage={uploaderImages.additional}
              multiple={true}
              disabled={!hasRequiredImages}
              loading={imagesSelected?.some(img => img.index === 5 && img.upload === "loading")}
              deleting={fetchingDeleteImage === "loading"}
            />
          </div>
        </Tooltip>

        {additionalUploaders.map((index) => (
          <div key={index} className={styles.uploaderItem}>
            <h3>Foto adicional</h3>
            <CustomFileUploader
              values={imagesSelected?.filter(img => img.index === (6 + index)) || []}
              onChange={(files: ImageFile[]) => onChangeFileUploader(files, 6 + index)}
              onDeleteItem={handleDeleteImage}
              className={styles.fileUploader}
              placeholderImage={uploaderImages.additional}
              loading={imagesSelected?.some(img => img.index === (6 + index) && img.upload === "loading")}
              deleting={fetchingDeleteImage === "loading"}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditImageManager;